import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useBookingScreenEntry } from '../lib/useBookingScreenEntry'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, CreditCard, Minus, Plus, Users, X } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'

function formatDateRange(checkInDate, checkOutDate) {
  if (!checkInDate || !checkOutDate) return null
  return `${checkInDate} → ${checkOutDate}`
}

function formatDisplayDate(dateString, locale) {
  if (!dateString) return ''
  const date = new Date(`${dateString}T00:00:00`)
  if (Number.isNaN(date.getTime())) return dateString
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date)
}

function RoomTypeCard({ roomType, onSelect }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const hero = roomType?.heroPhotoUrl || roomType?.photoUrls?.[0] || null
  const nightlyRate = Number(roomType?.nightlyRate || 0)
  const currencyCode = roomType?.currencyCode || 'GBP'
  const hasRate = Number.isFinite(nightlyRate) && nightlyRate > 0
  const availableRoomCount = Number.parseInt(roomType?.availableRoomCount, 10)
  const availabilityCount = Number.isFinite(availableRoomCount)
    ? Math.max(0, availableRoomCount)
    : (Number(roomType?.roomCount) || 0)

  return (
    <button type="button" className="mp-accommodation-card" onClick={onSelect}>
      <div className="mp-accommodation-card__media">
        {hero ? (
          <img src={hero} alt={roomType.roomType} />
        ) : (
          <div className="mp-accommodation-card__placeholder">{memberText("portalPresentation.member_visual_coming_soon")}</div>
        )}
      </div>
      <div className="mp-accommodation-card__body">
        <div className="mp-accommodation-card__eyebrow">{memberText("portalPresentation.member_room_type")}</div>
        <div className="mp-accommodation-card__title">{roomType.roomType}</div>
        <div className="mp-accommodation-card__meta">
          <Users size={14} />
          <span>{availabilityCount} {memberText("portalPresentation.member_rooms_available")}</span>
        </div>
        {hasRate ? <div className="mp-accommodation-card__rate">{memberText("portalPresentation.member_from")} {currencyCode} {new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(nightlyRate)} {memberText("portalPresentation.member__night")}</div> : null}
        <div className="mp-chip">{memberText("portalPresentation.member_view_details")}</div>
      </div>
    </button>
  )
}

export default function MemberAccommodationsPage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const SOFT_MAX_ROOMS = 12
  const { memberPortalApiGet } = useMemberPortalApi()
  const navigate = useNavigate()

  const [roomTypes, setRoomTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [checkInDate, setCheckInDate] = useState('')
  const [checkOutDate, setCheckOutDate] = useState('')
  const [guestsCount, setGuestsCount] = useState(2)
  const [roomGuests, setRoomGuests] = useState([{ adults: 2, children: 0 }])
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isGuestsPanelOpen, setIsGuestsPanelOpen] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchError, setSearchError] = useState(null)

  const [selectedType, setSelectedType] = useState(null)
  const roomScreenRef = useRef(null)
  useBookingScreenEntry(selectedType?.id || selectedType?.roomType || 'closed', roomScreenRef)
  const [carouselIndex, setCarouselIndex] = useState(0)

  const resultsRef = useRef(null)
  const guestsPanelRef = useRef(null)
  const datePickerRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    async function loadRoomTypes() {
      if (!clubCode) return
      setLoading(true)
      setError(null)

      try {
        const params = new URLSearchParams({
          rooms: JSON.stringify(roomGuests),
        })
        if (checkInDate) params.set('checkInDate', checkInDate)
        if (checkOutDate) params.set('checkOutDate', checkOutDate)
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/hotel/room-types?${params.toString()}`)
        const json = await res.json().catch(() => null)

        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_room_types')
        }

        const types = Array.isArray(json?.roomTypes) ? json.roomTypes : []
        if (!cancelled) {
          setRoomTypes(types)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load room types', err)
          setError("portalPresentation.member_we_could_not_load_accommodations_right_now_please_try_again_shortly")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadRoomTypes()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet, roomGuests, checkInDate, checkOutDate])

  useEffect(() => {
    function handlePointerDown(event) {
      if (isGuestsPanelOpen && guestsPanelRef.current && !guestsPanelRef.current.contains(event.target)) {
        setIsGuestsPanelOpen(false)
      }
      if (isDatePickerOpen && datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isGuestsPanelOpen, isDatePickerOpen])

  useEffect(() => {
    const totalGuests = roomGuests.reduce((sum, room) => sum + room.adults + room.children, 0)
    setGuestsCount(Math.max(1, totalGuests))
  }, [roomGuests])

  const orderedRoomTypes = useMemo(
    () => [...roomTypes].sort((a, b) => a.roomType.localeCompare(b.roomType)),
    [roomTypes]
  )

  const nights = useMemo(() => {
    if (!checkInDate || !checkOutDate || checkOutDate <= checkInDate) return 1
    const from = new Date(`${checkInDate}T00:00:00.000Z`)
    const to = new Date(`${checkOutDate}T00:00:00.000Z`)
    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000))
  }, [checkInDate, checkOutDate])

  const photoStack = useMemo(() => {
    if (!selectedType) return []
    return (selectedType.photoUrls || []).slice(0, 10)
  }, [selectedType])

  const totalAdults = useMemo(() => roomGuests.reduce((sum, room) => sum + room.adults, 0), [roomGuests])
  const totalChildren = useMemo(() => roomGuests.reduce((sum, room) => sum + room.children, 0), [roomGuests])

  const currentPhoto = selectedType && photoStack.length > 0 ? photoStack[carouselIndex] : null
  const nightlyRate = useMemo(() => Number(selectedType?.nightlyRate || 0), [selectedType?.nightlyRate])
  const currencyCode = useMemo(() => selectedType?.currencyCode || 'GBP', [selectedType?.currencyCode])
  const totalEstimate = useMemo(
    () => Math.max(0, Number((nightlyRate * nights * roomGuests.length).toFixed(2))),
    [nightlyRate, nights, roomGuests.length]
  )
  const hasValidRate = Number.isFinite(nightlyRate) && nightlyRate > 0
  const hasValidSearch = Boolean(checkInDate && checkOutDate && checkOutDate > checkInDate && guestsCount > 0)
  const canContinue = selectedType && hasValidSearch

  const dateRangeLabel = checkInDate && checkOutDate
    ? `${formatDisplayDate(checkInDate, locale)} → ${formatDisplayDate(checkOutDate, locale)}`
    : memberText("portalPresentation.member_select_your_stay_dates")

  const guestsSummary = memberText("portalPresentation.member__adults_adults_children_children_rooms_rooms", { adults: totalAdults, children: totalChildren, rooms: roomGuests.length })

  const handleOpenType = (type) => {
    setSelectedType(type)
    setCarouselIndex(0)
  }

  const closeModal = () => {
    setSelectedType(null)
    setCarouselIndex(0)
  }

  const adjustRoomCount = (nextCount) => {
    // Soft UX guard only; backend availability/occupancy validation remains source of truth.
    const clampedCount = Math.max(1, Math.min(SOFT_MAX_ROOMS, nextCount))
    setRoomGuests((prev) => {
      if (clampedCount === prev.length) return prev
      if (clampedCount < prev.length) return prev.slice(0, clampedCount)
      return [...prev, ...Array.from({ length: clampedCount - prev.length }, () => ({ adults: 2, children: 0 }))]
    })
  }

  const adjustRoomGuest = (roomIndex, field, delta) => {
    setRoomGuests((prev) => prev.map((room, index) => {
      if (index !== roomIndex) return room
      const minimum = field === 'adults' ? 1 : 0
      const nextValue = Math.max(minimum, room[field] + delta)
      return { ...room, [field]: nextValue }
    }))
  }

  const handleSearch = () => {
    setSearchError(null)

    if (!checkInDate || !checkOutDate) {
      setSearchError("portalPresentation.member_please_choose_a_check_in_and_check_out_date")
      return
    }

    if (checkOutDate <= checkInDate) {
      setSearchError("portalPresentation.member_check_out_must_be_after_check_in")
      return
    }

    if (guestsCount <= 0) {
      setSearchError("portalPresentation.member_please_add_at_least_one_guest")
      return
    }

    setHasSearched(true)
    setIsDatePickerOpen(false)
    setIsGuestsPanelOpen(false)

    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const proceedToBooking = () => {
    if (!canContinue || !hasValidRate) return

    const roomsPayload = JSON.stringify(roomGuests)
    const params = new URLSearchParams({
      roomType: selectedType?.roomType || '',
      checkInDate,
      checkOutDate,
      guestsCount: String(Math.max(1, Number.parseInt(guestsCount, 10) || 1)),
      roomCount: String(roomGuests.length),
      rooms: roomsPayload,
    })

    navigate(`/${clubCode}/accommodations/book?${params.toString()}`, {
      state: {
        roomType: selectedType?.roomType || null,
        rooms: roomGuests,
      },
    })
  }

  return (
    <div className="mp-tee-booking mp-accommodation-flow">
      <div className="mp-accommodation-hero">
        <p className="mp-eyebrow">{memberText("portalPresentation.member_accommodations")}</p>
        <h3> {memberText("portalPresentation.member_stay_in")} <strong>{memberText("portalPresentation.member_style")}</strong>
        </h3>
        <p className="mp-text-muted">{memberText("portalPresentation.member_choose_your_dates_and_room_setup_to_see_currently_available_suites")}</p>
      </div>

      {error ? <div className="mp-inline-error">{memberText(error)}</div> : null}

      <section className="mp-accommodation-search mp-accommodation-search--sticky">
        <div className="mp-accommodation-search__grid">
          <div className="mp-accommodation-search__field" ref={datePickerRef}>
            <button type="button" className="mp-accommodation-search__control" onClick={() => {
              setIsDatePickerOpen((prev) => !prev)
              setIsGuestsPanelOpen(false)
            }}>
              <span className="mp-accommodation-search__label">{memberText("portalPresentation.member_check_in_check_out")}</span>
              <span className="mp-accommodation-search__value">{dateRangeLabel}</span>
              <Calendar size={16} />
            </button>
            {isDatePickerOpen ? (
              <div className="mp-accommodation-popover">
                <label>
                  <span>{memberText("portalPresentation.member_check_in")}</span>
                  <input type="date" value={checkInDate} onChange={(event) => setCheckInDate(event.target.value)} />
                </label>
                <label>
                  <span>{memberText("portalPresentation.member_check_out")}</span>
                  <input
                    type="date"
                    min={checkInDate || undefined}
                    value={checkOutDate}
                    onChange={(event) => setCheckOutDate(event.target.value)}
                  />
                </label>
              </div>
            ) : null}
          </div>

          <div className="mp-accommodation-search__field" ref={guestsPanelRef}>
            <button type="button" className="mp-accommodation-search__control" onClick={() => {
              setIsGuestsPanelOpen((prev) => !prev)
              setIsDatePickerOpen(false)
            }}>
              <span className="mp-accommodation-search__label">{memberText("portalPresentation.member_guests_rooms")}</span>
              <span className="mp-accommodation-search__value">{guestsSummary}</span>
              <ChevronDown size={16} />
            </button>
            {isGuestsPanelOpen ? (
              <div className="mp-accommodation-popover mp-accommodation-popover--guests">
                <div className="mp-accommodation-counter-row">
                  <div>
                    <p className="mp-value">{memberText("portalPresentation.member_rooms_caption")}</p>
                    <p className="mp-text-muted">{memberText("portalPresentation.member_add_rooms_as_needed")}</p>
                  </div>
                  <div className="mp-stepper">
                    <button type="button" onClick={() => adjustRoomCount(roomGuests.length - 1)} aria-label={memberText("portalPresentation.member_decrease_rooms")}>
                      <Minus size={14} />
                    </button>
                    <span>{roomGuests.length}</span>
                    <button type="button" onClick={() => adjustRoomCount(roomGuests.length + 1)} aria-label={memberText("portalPresentation.member_increase_rooms")}>
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                {roomGuests.map((room, index) => (
                  <div key={`room-${index}`} className="mp-accommodation-room-editor">
                    <p className="mp-value">{memberText("portalPresentation.member_room")} {index + 1}</p>
                    <div className="mp-accommodation-counter-row">
                      <span>{memberText("portalPresentation.member_adults_2")}</span>
                      <div className="mp-stepper">
                        <button type="button" onClick={() => adjustRoomGuest(index, 'adults', -1)} aria-label={memberText("portalPresentation.member_decrease_adults_for_room_room", { room: index + 1 })}>
                          <Minus size={14} />
                        </button>
                        <span>{room.adults}</span>
                        <button type="button" onClick={() => adjustRoomGuest(index, 'adults', 1)} aria-label={memberText("portalPresentation.member_increase_adults_for_room_room", { room: index + 1 })}>
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="mp-accommodation-counter-row">
                      <span>{memberText("portalPresentation.member_children_2")}</span>
                      <div className="mp-stepper">
                        <button type="button" onClick={() => adjustRoomGuest(index, 'children', -1)} aria-label={memberText("portalPresentation.member_decrease_children_for_room_room", { room: index + 1 })}>
                          <Minus size={14} />
                        </button>
                        <span>{room.children}</span>
                        <button type="button" onClick={() => adjustRoomGuest(index, 'children', 1)} aria-label={memberText("portalPresentation.member_increase_children_for_room_room", { room: index + 1 })}>
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <button type="button" className="mp-primary mp-accommodation-search__cta" disabled={!hasValidSearch} onClick={handleSearch}> {memberText("portalPresentation.member_check_availability")} </button>
        </div>
        {searchError ? <div className="mp-inline-error">{memberText(searchError)}</div> : null}
      </section>

      {hasSearched ? (
        <section className="mp-card mp-accommodation-results" ref={resultsRef}>
          <div className="mp-card__header">
            <div>
              <p className="mp-eyebrow">{memberText("portalPresentation.member_step_2")}</p>
              <h4>{memberText("portalPresentation.member_available_room_types")}</h4>
            </div>
          </div>
          <div className="mp-card__body">
            {loading ? (
              <p className="mp-text-muted">{memberText("portalPresentation.member_loading_suites_and_rooms")}</p>
            ) : orderedRoomTypes.filter((roomType) => roomType.isEligibleForRequest !== false).length === 0 ? (
              <p className="mp-text-muted">{memberText("portalPresentation.member_no_rooms_are_available_to_request_right_now")}</p>
            ) : (
              <div className="mp-accommodation-card-grid">
                {orderedRoomTypes.filter((roomType) => roomType.isEligibleForRequest !== false).map((roomType) => (
                  <RoomTypeCard key={roomType.roomType} roomType={roomType} onSelect={() => handleOpenType(roomType)} />
                ))}
              </div>
            )}
          </div>
        </section>
      ) : null}

      {selectedType ? (
        <div className="mp-modal" role="dialog" aria-modal="true" aria-label={memberText("portalPresentation.member_accommodation_room_details")}>
          <button className="mp-modal__backdrop" type="button" aria-label={memberText("portalPresentation.member_close")} onClick={closeModal} />
          <div className="mp-modal__panel mp-accommodation-modal__panel" ref={roomScreenRef}>
            <div className="mp-modal__header">
              <div>
                <p className="mp-eyebrow">{selectedType.roomType}</p>
                <h4>{memberText("portalPresentation.member_room_details")}</h4>
              </div>
              <button type="button" className="mp-icon-btn" onClick={closeModal} aria-label={memberText("portalPresentation.member_close_room_details")}>
                <X size={16} />
              </button>
            </div>

            <div className="mp-accommodation-modal">
              <div className="mp-accommodation-carousel">
                {currentPhoto ? (
                  <img src={currentPhoto} alt={selectedType.roomType} />
                ) : (
                  <div className="mp-accommodation-card__placeholder">{memberText("portalPresentation.member_photos_coming_soon")}</div>
                )}
                {photoStack.length > 1 ? (
                  <div className="mp-accommodation-carousel__controls">
                    <button type="button" onClick={() => setCarouselIndex((prev) => (prev - 1 + photoStack.length) % photoStack.length)} aria-label={memberText("portalPresentation.member_previous_photo")}>
                      <ChevronLeft size={18} />
                    </button>
                    <button type="button" onClick={() => setCarouselIndex((prev) => (prev + 1) % photoStack.length)} aria-label={memberText("portalPresentation.member_next_photo")}>
                      <ChevronRight size={18} />
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="mp-accommodation-actions">
                <div className="mp-summary-section">
                  <div className="mp-summary-row">
                    <Calendar size={16} />
                    <div>
                      <p className="mp-label">{memberText("portalPresentation.member_dates")}</p>
                      <p className="mp-value">{formatDateRange(checkInDate, checkOutDate) || memberText("portalPresentation.member_select_dates")}</p>
                    </div>
                  </div>
                  <div className="mp-summary-row">
                    <Users size={16} />
                    <div>
                      <p className="mp-label">{memberText("portalPresentation.member_stay_guests")}</p>
                      <p className="mp-value">{Math.max(1, Number.parseInt(guestsCount, 10) || 1)}</p>
                    </div>
                  </div>
                  <div className="mp-summary-row">
                    <CreditCard size={16} />
                    <div>
                      <p className="mp-label">{memberText("portalPresentation.member_total_estimate")}</p>
                      <p className="mp-value">{currencyCode} {new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalEstimate)}</p>
                    </div>
                  </div>
                </div>

                <div className="mp-modal__footer mp-accommodation-footer">
                  <div className="mp-accommodation-footer__actions">
                    <button type="button" className="mp-ghost" onClick={closeModal}> {memberText("portalPresentation.member_cancel")} </button>
                    <button type="button" className="mp-primary" onClick={proceedToBooking} disabled={!canContinue || !hasValidRate}> {memberText("portalPresentation.member_continue_to_booking")} </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
